import { Suspense, ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import RequireStaff from "./components/admin/RequireStaff";

import Index from "./pages/Index";
import { lazyWithRetry as lazy } from "./lib/lazyWithRetry";

// Lazy load all other routes
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Parents = lazy(() => import("./pages/Parents"));
const Sponsors = lazy(() => import("./pages/Sponsors"));
const Schedules = lazy(() => import("./pages/Schedules"));
const Standings = lazy(() => import("./pages/Standings"));
const LeagueRules = lazy(() => import("./pages/LeagueRules"));
const LeagueOrganizing = lazy(() => import("./pages/LeagueOrganizing"));
const Benefits = lazy(() => import("./pages/Benefits"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Register = lazy(() => import("./pages/Register"));
const RegistrationSuccess = lazy(() => import("./pages/RegistrationSuccess"));
const HowToPlay = lazy(() => import("./pages/resources/HowToPlay"));
const PositionGuide = lazy(() => import("./pages/resources/PositionGuide"));
const YouthSafety = lazy(() => import("./pages/resources/YouthSafety"));
const CoachingGuide = lazy(() => import("./pages/resources/CoachingGuide"));
const Playbooks = lazy(() => import("./pages/resources/Playbooks"));
const PracticePlans = lazy(() => import("./pages/resources/PracticePlans"));
const RotationSheets = lazy(() => import("./pages/resources/RotationSheets"));
const StatSheets = lazy(() => import("./pages/resources/StatSheets"));
const BecomeCoach = lazy(() => import("./pages/get-involved/BecomeCoach"));
const BecomeVolunteer = lazy(() => import("./pages/get-involved/BecomeVolunteer"));
const BecomeOfficial = lazy(() => import("./pages/get-involved/BecomeOfficial"));
const BecomeSponsor = lazy(() => import("./pages/get-involved/BecomeSponsor"));
const JoinCommunity = lazy(() => import("./pages/JoinCommunity"));
const Shop = lazy(() => import("./pages/Shop"));
const Install = lazy(() => import("./pages/Install"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const FlagFootballBadAxe = lazy(() => import("./pages/locations/FlagFootballBadAxe"));
const FlagFootballCassCity = lazy(() => import("./pages/locations/FlagFootballCassCity"));
const FlagFootballHarborBeach = lazy(() => import("./pages/locations/FlagFootballHarborBeach"));
const FlagFootballUbly = lazy(() => import("./pages/locations/FlagFootballUbly"));
const StandingsAdmin = lazy(() => import("./pages/admin/StandingsAdmin"));
const SponsorAnalytics = lazy(() => import("./pages/admin/SponsorAnalytics"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ScheduleAdmin = lazy(() => import("./pages/admin/ScheduleAdmin"));
const RegistrationAdmin = lazy(() => import("./pages/admin/RegistrationAdmin"));
const AnnouncementsAdmin = lazy(() => import("./pages/admin/AnnouncementsAdmin"));
const CoachAdmin = lazy(() => import("./pages/admin/CoachAdmin"));
const EmailAdmin = lazy(() => import("./pages/admin/EmailAdmin"));
const EmailSequences = lazy(() => import("./pages/admin/EmailSequences"));
const VolunteerAdmin = lazy(() => import("./pages/admin/VolunteerAdmin"));
const OfficialsAdmin = lazy(() => import("./pages/admin/OfficialsAdmin"));
const BoardMembersAdmin = lazy(() => import("./pages/admin/BoardMembersAdmin"));
const Board = lazy(() => import("./pages/Board"));
const WaitlistAdmin = lazy(() => import("./pages/admin/WaitlistAdmin"));
const EquipmentAdmin = lazy(() => import("./pages/admin/EquipmentAdmin"));
const FinancialAdmin = lazy(() => import("./pages/admin/FinancialAdmin"));
const MediaAdmin = lazy(() => import("./pages/admin/MediaAdmin"));
const FlyersAdmin = lazy(() => import("./pages/admin/FlyersAdmin"));
const PhotoSubmissionsAdmin = lazy(() => import("./pages/admin/PhotoSubmissionsAdmin"));
const SeasonArchive = lazy(() => import("./pages/admin/SeasonArchive"));
const ErrorReports = lazy(() => import("./pages/admin/ErrorReports"));
const ChangeRequestsAdmin = lazy(() => import("./pages/admin/ChangeRequestsAdmin"));
const HomepageAdmin = lazy(() => import("./pages/admin/HomepageAdmin"));
const ImportAdmin = lazy(() => import("./pages/admin/ImportAdmin"));
const AnalyticsDashboard = lazy(() => import("./pages/admin/AnalyticsDashboard"));
const PlayerCRM = lazy(() => import("./pages/admin/PlayerCRM"));
const PlayerProfile = lazy(() => import("./pages/admin/PlayerProfile"));

const FileManager = lazy(() => import("./pages/admin/FileManager"));
const SponsoredPlayersAdmin = lazy(() => import("./pages/admin/SponsoredPlayersAdmin"));
const SponsorsAdmin = lazy(() => import("./pages/admin/SponsorsAdmin"));
const ComponentLibrary = lazy(() => import("./pages/admin/ComponentLibrary"));
const BatchImportAdmin = lazy(() => import("./pages/admin/BatchImportAdmin"));
const BackfillRegistrations = lazy(() => import("./pages/admin/BackfillRegistrations"));
const IntegrationsHub = lazy(() => import("./pages/admin/IntegrationsHub"));
const CalendarAdmin = lazy(() => import("./pages/admin/CalendarAdmin"));
const ScheduleManager = lazy(() => import("./pages/admin/ScheduleManager"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const ParentPortal = lazy(() => import("./pages/ParentPortal"));
const SponsorDirectoryPage = lazy(() => import("./pages/SponsorDirectoryPage"));
const AllTimeSponsors = lazy(() => import("./pages/AllTimeSponsors"));
const SponsorQRCodesPage = lazy(() => import("./pages/SponsorQRCodesPage"));
const QRCodeDownload = lazy(() => import("./pages/QRCodeDownload"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const QRRedirect = lazy(() => import("./pages/QRRedirect"));
const Flyer = lazy(() => import("./pages/Flyer"));
const PaymentThankYou = lazy(() => import("./pages/PaymentThankYou"));
const SponsorPortal = lazy(() => import("./pages/SponsorPortal"));
const SponsorPaymentThankYou = lazy(() => import("./pages/SponsorPaymentThankYou"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const SponsorSupport = lazy(() => import("./pages/admin/SponsorSupport"));
const RefScoreEntry = lazy(() => import("./pages/RefScoreEntry"));
const GameDayDashboard = lazy(() => import("./pages/admin/GameDayDashboard"));
const InboxAdmin = lazy(() => import("./pages/admin/InboxAdmin"));
const VolunteerPipeline = lazy(() => import("./pages/admin/VolunteerPipeline"));
const HelperMasterList = lazy(() => import("./pages/admin/HelperMasterList"));
const LocalDominanceAuditPage = lazy(() => import("./pages/admin/LocalDominanceAuditPage"));
const Events = lazy(() => import("./pages/Events"));
const CoachPortal = lazy(() => import("./pages/CoachPortal"));
const Tournaments = lazy(() => import("./pages/Tournaments"));
const TournamentBracket = lazy(() => import("./pages/TournamentBracket"));
const TournamentBracketPrint = lazy(() => import("./pages/TournamentBracketPrint"));

const queryClient = new QueryClient();

// Ref-mode auto-redirect: if installed from /ref/scores, always open there
const isStandalone =
  window.matchMedia("(display-mode: standalone)").matches ||
  (window.navigator as any).standalone;
const isRefMode = localStorage.getItem("ref-mode") === "true";

if (isStandalone && isRefMode && !window.location.pathname.startsWith("/ref/")) {
  window.location.replace("/ref/scores");
}

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/parents" element={<Parents />} />
            <Route path="/sponsors" element={<Sponsors />} />
            <Route path="/schedules" element={<Schedules />} />
            <Route path="/standings" element={<Standings />} />
            <Route path="/league-rules" element={<LeagueRules />} />
            <Route path="/league-organizing" element={<LeagueOrganizing />} />
            <Route path="/benefits" element={<Benefits />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register/spring" element={<Register />} />
            <Route path="/register/fall" element={<Register />} />
            <Route path="/register/success" element={<RegistrationSuccess />} />
            <Route path="/resources/how-to-play" element={<HowToPlay />} />
            <Route path="/resources/position-guide" element={<PositionGuide />} />
            <Route path="/resources/youth-safety" element={<YouthSafety />} />
            <Route path="/resources/coaching-guide" element={<CoachingGuide />} />
            <Route path="/resources/playbooks" element={<Playbooks />} />
            <Route path="/resources/practice-plans" element={<PracticePlans />} />
            <Route path="/resources/rotation-sheets" element={<RotationSheets />} />
            <Route path="/resources/stat-sheets" element={<StatSheets />} />
            <Route path="/get-involved/coach" element={<BecomeCoach />} />
            <Route path="/get-involved/volunteer" element={<BecomeVolunteer />} />
            <Route path="/get-involved/official" element={<BecomeOfficial />} />
            <Route path="/get-involved/sponsor" element={<BecomeSponsor />} />
            <Route path="/join" element={<JoinCommunity />} />
            <Route path="/join-community" element={<JoinCommunity />} />
            <Route path="/get-involved/join" element={<JoinCommunity />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/install" element={<Install />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/flag-football-bad-axe" element={<FlagFootballBadAxe />} />
            <Route path="/flag-football-cass-city" element={<FlagFootballCassCity />} />
            <Route path="/flag-football-harbor-beach" element={<FlagFootballHarborBeach />} />
            <Route path="/flag-football-ubly" element={<FlagFootballUbly />} />
            <Route path="/admin" element={<RequireStaff><AdminDashboard /></RequireStaff>} />
            <Route path="/admin/calendar" element={<RequireStaff><CalendarAdmin /></RequireStaff>} />
            <Route path="/admin/schedule-manager" element={<RequireStaff><ScheduleManager /></RequireStaff>} />
            <Route path="/admin/tournaments" element={<RequireStaff><Navigate to="/admin/schedule-manager?tab=tournaments" replace /></RequireStaff>} />
            <Route path="/admin/schedules" element={<RequireStaff><ScheduleAdmin /></RequireStaff>} />
            <Route path="/admin/registrations" element={<RequireStaff><RegistrationAdmin /></RequireStaff>} />
            <Route path="/admin/announcements" element={<RequireStaff><AnnouncementsAdmin /></RequireStaff>} />
            <Route path="/admin/coaches" element={<RequireStaff><CoachAdmin /></RequireStaff>} />
            <Route path="/admin/emails" element={<RequireStaff><EmailAdmin /></RequireStaff>} />
            <Route path="/admin/email-sequences" element={<RequireStaff><EmailSequences /></RequireStaff>} />
            <Route path="/admin/volunteers" element={<RequireStaff><VolunteerAdmin /></RequireStaff>} />
            <Route path="/admin/officials" element={<RequireStaff><OfficialsAdmin /></RequireStaff>} />
            <Route path="/admin/board-members" element={<RequireStaff><BoardMembersAdmin /></RequireStaff>} />
            <Route path="/board" element={<Board />} />
            <Route path="/admin/waitlist" element={<RequireStaff><WaitlistAdmin /></RequireStaff>} />
            <Route path="/admin/equipment" element={<RequireStaff><EquipmentAdmin /></RequireStaff>} />
            <Route path="/admin/finances" element={<RequireStaff><FinancialAdmin /></RequireStaff>} />
            <Route path="/admin/media" element={<RequireStaff><MediaAdmin /></RequireStaff>} />
            <Route path="/admin/flyers" element={<RequireStaff><FlyersAdmin /></RequireStaff>} />
            <Route path="/admin/photo-submissions" element={<RequireStaff><PhotoSubmissionsAdmin /></RequireStaff>} />
            <Route path="/admin/archives" element={<RequireStaff><SeasonArchive /></RequireStaff>} />
            <Route path="/admin/error-reports" element={<RequireStaff><ErrorReports /></RequireStaff>} />
            <Route path="/admin/change-requests" element={<RequireStaff><ChangeRequestsAdmin /></RequireStaff>} />
            <Route path="/admin/homepage" element={<RequireStaff><HomepageAdmin /></RequireStaff>} />
            <Route path="/admin/import" element={<RequireStaff><ImportAdmin /></RequireStaff>} />
            <Route path="/admin/analytics" element={<RequireStaff><AnalyticsDashboard /></RequireStaff>} />
            <Route path="/admin/crm" element={<RequireStaff><PlayerCRM /></RequireStaff>} />
            <Route path="/admin/players/:id" element={<RequireStaff><PlayerProfile /></RequireStaff>} />
            <Route path="/admin/standings" element={<RequireStaff><StandingsAdmin /></RequireStaff>} />
            <Route path="/admin/sponsor-analytics" element={<RequireStaff><SponsorAnalytics /></RequireStaff>} />
            
            <Route path="/admin/files" element={<RequireStaff><FileManager /></RequireStaff>} />
            <Route path="/admin/sponsored-players" element={<RequireStaff><SponsoredPlayersAdmin /></RequireStaff>} />
            <Route path="/admin/sponsors" element={<RequireStaff><SponsorsAdmin /></RequireStaff>} />
            <Route path="/admin/components" element={<RequireStaff><ComponentLibrary /></RequireStaff>} />
            <Route path="/admin/batch-import" element={<RequireStaff><BatchImportAdmin /></RequireStaff>} />
            <Route path="/admin/backfill-registrations" element={<RequireStaff><BackfillRegistrations /></RequireStaff>} />
            <Route path="/admin/integrations" element={<RequireStaff><IntegrationsHub /></RequireStaff>} />
            <Route path="/admin/users" element={<RequireStaff><UserManagement /></RequireStaff>} />
            <Route path="/admin/sponsor-support" element={<RequireStaff><SponsorSupport /></RequireStaff>} />
            <Route path="/my-team" element={<ParentPortal />} />
            <Route path="/directory" element={<SponsorDirectoryPage />} />
            <Route path="/sponsors/all-time" element={<AllTimeSponsors />} />
            <Route path="/sponsors/qr-codes" element={<SponsorQRCodesPage />} />
            <Route path="/qr-code" element={<QRCodeDownload />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/go" element={<QRRedirect />} />
            <Route path="/flyer" element={<Flyer />} />
            <Route path="/payment-thank-you" element={<PaymentThankYou />} />
            <Route path="/pay" element={<PaymentPage />} />
            <Route path="/sponsor-portal" element={<SponsorPortal />} />
            <Route path="/sponsor-payment-thank-you" element={<SponsorPaymentThankYou />} />
            <Route path="/ref/scores" element={<RefScoreEntry />} />
            <Route path="/admin/game-day" element={<RequireStaff><GameDayDashboard /></RequireStaff>} />
            <Route path="/admin/inbox" element={<RequireStaff><InboxAdmin /></RequireStaff>} />
            <Route path="/admin/volunteer-pipeline" element={<RequireStaff><VolunteerPipeline /></RequireStaff>} />
            <Route path="/admin/helper-master-list" element={<RequireStaff><HelperMasterList /></RequireStaff>} />
            <Route path="/admin/local-dominance-audit" element={<RequireStaff><LocalDominanceAuditPage /></RequireStaff>} />
            <Route path="/events" element={<Events />} />
            <Route path="/coach-portal" element={<CoachPortal />} />
            <Route path="/tournaments" element={<Tournaments />} />
            <Route path="/tournaments/:id" element={<TournamentBracket />} />
            <Route path="/tournaments/:id/print" element={<TournamentBracketPrint />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
